// import validation lib
import * as yup from 'yup';

// import i18n
import { t } from 'i18next';

// import type
import type { TFunction } from 'i18next';

// a small helper for the commonly repeated error message:
const requiredMsg = (t: TFunction, translationKey: string) =>
  t('validationFieldRequired', { name: t(translationKey) });

// reusable partial fields
const arrangementTypesField = () => ({
  arrangementTypes: yup
    .object()
    .shape({
      name: yup
        .string()
        .required(requiredMsg(t, 'travelArrangements.arrangementTypes'))
    })
    .required(requiredMsg(t, 'travelArrangements.arrangementTypes'))
    .typeError(requiredMsg(t, 'travelArrangements.arrangementTypes'))
});

const trvSetField = () => ({
  trvSet: yup
    .object()
    .shape({
      description: yup
        .string()
        .required(requiredMsg(t, 'travelArrangements.compensationTypeName'))
    })
    .typeError(requiredMsg(t, 'travelArrangements.compensationTypeName'))
});

const validField = () => ({
  valid: yup.object().shape({
    from: yup
      .date()
      .nullable()
      .default(null)
      .typeError(requiredMsg(t, 'validFrom'))
      .required(requiredMsg(t, 'validFrom')),
    to: yup
      .date()
      .nullable()
      .default(null)
      .typeError(requiredMsg(t, 'validTo'))
      .required(requiredMsg(t, 'validTo'))
      .min(yup.ref('from'), t('validationStartAndEndDateDifference'))
  })
});

const waNrField = () => ({
  waNr: yup
    .object()
    .shape({
      nrString: yup.string().nullable()
    })
    .nullable()
    .typeError(requiredMsg(t, 'employment'))
});

const distanceKmField = () => ({
  distanceKm: yup.number().nullable()
});

const streetField = () => ({
  streetName: yup.string().nullable().required(requiredMsg(t, 'street'))
});

const houseNumberField = () => ({
  houseNumber: yup.string().nullable().required(requiredMsg(t, 'houseNumber'))
});

const cityField = () => ({
  city: yup.string().nullable().required(requiredMsg(t, 'city'))
});

const postalCodeField = () => ({
  postalCode: yup.string().nullable().required(requiredMsg(t, 'postalCode'))
});

const countryCodeField = () => ({
  countryCode: yup
    .object()
    .shape({
      label: yup.string().required(requiredMsg(t, 'country'))
    })
    .required(requiredMsg(t, 'country'))
    .typeError(requiredMsg(t, 'country'))
});

const travelExpensesNominalField = () => ({
  travelExpensesNominal: yup
    .number()
    .nullable()
    .required(requiredMsg(t, 'travelArrangements.amount'))
    .min(0, requiredMsg(t, 'travelArrangements.amount'))
});

// Schema's for each of the different types of travel arrangements
export const NoCompensationSchema = () =>
  yup.object().shape({
    ...arrangementTypesField(),
    ...validField(),
    ...waNrField()
  });

export const VariableBasedOnAddressSchema = () =>
  yup.object().shape({
    ...arrangementTypesField(),
    ...trvSetField(),
    ...validField(),
    ...waNrField(),
    ...distanceKmField(),
    ...streetField(),
    ...houseNumberField(),
    ...postalCodeField(),
    ...cityField(),
    ...countryCodeField()
  });

export const VariableBasedOnPostalCodeSchema = () =>
  yup.object().shape({
    ...arrangementTypesField(),
    ...trvSetField(),
    ...validField(),
    ...waNrField(),
    ...distanceKmField(),
    ...postalCodeField(),
    ...countryCodeField()
  });

export const VariableBasedOnDistanceSchema = () =>
  yup.object().shape({
    ...arrangementTypesField(),
    ...trvSetField(),
    ...validField(),
    ...waNrField(),
    ...distanceKmField()
  });

export const VariableBasedOnDistanceInServiceSchema = () =>
  yup.object().shape({
    ...arrangementTypesField(),
    ...trvSetField(),
    ...validField(),
    ...waNrField()
  });

export const FixedAmountSchema = () =>
  yup.object().shape({
    ...arrangementTypesField(),
    ...validField(),
    ...waNrField(),
    ...distanceKmField(),
    ...travelExpensesNominalField()
  });
